/**
 * ClinicSummaryCards — сводка отчётов клиники: деньги (нетто/приход/возвраты,
 * начислено/скидки/долг/средний чек), приёмы по статусам, пациенты.
 */

import type {ReactNode} from 'react';

import type {ClinicReportSummary} from '@services/reports';
import {formatNumber} from '@utils/format';

type Props = {s: ClinicReportSummary};

export const ClinicSummaryCards = ({s}: Props) => (
  <div className="grid grid-cols-3 gap-4 max-lg:grid-cols-1">
    <Card title="Деньги за период">
      <Big value={s.revenue.net} label="нетто, сом" />
      <Line label="Приход">{formatNumber(s.revenue.income)}</Line>
      <Line label="Возвраты" tone="warn">
        −{formatNumber(s.revenue.refund)}
      </Line>
      <Line label="Средний чек">{formatNumber(s.averageCheck)}</Line>
    </Card>

    <Card title="Начисления">
      <Big value={s.charged} label="начислено, сом" />
      <Line label="Скидки">−{formatNumber(s.discounts)}</Line>
      <Line label="Долг пациентов" tone={s.debt > 0 ? 'warn' : undefined}>
        {formatNumber(s.debt)}
      </Line>
    </Card>

    <Card title="Приёмы и пациенты">
      <Big value={s.appointments.completed} label={`завершено из ${s.appointments.total}`} />
      <Line label="Отменено · не пришли">
        {s.appointments.cancelled} · {s.appointments.noShow}
      </Line>
      <Line label="Пациентов">
        {formatNumber(s.patients.total)}{' '}
        <span className="text-skr-faint">
          (новых {s.patients.new} · повторных {s.patients.returning})
        </span>
      </Line>
    </Card>
  </div>
);

const Card = ({title, children}: {title: string; children: ReactNode}) => (
  <div className="rounded-[15px] border border-skr-line bg-card px-[18px] pb-4 pt-[15px]">
    <div className="mb-2.5 text-[13.5px] font-semibold">{title}</div>
    {children}
  </div>
);

const Big = ({value, label}: {value: number; label: string}) => (
  <div className="mb-2.5 flex items-baseline gap-2">
    <span className="font-mono text-[26px] font-semibold -tracking-[.02em]">
      {formatNumber(value)}
    </span>
    <span className="text-xs text-muted-foreground">{label}</span>
  </div>
);

const Line = ({label, tone, children}: {label: string; tone?: 'warn'; children: ReactNode}) => (
  <div className="flex items-center justify-between border-t border-skr-hairline py-[7px] text-[12.5px] first:border-t-0">
    <span className="text-muted-foreground">{label}</span>
    <span className={`font-mono ${tone === 'warn' ? 'text-skr-warn' : 'text-skr-ink'}`}>
      {children}
    </span>
  </div>
);
